/**
 * voucher-code — the code's whole footprint on the event page: no row of its
 * own, ever.
 *
 * event-page.view.ts mounts it as the LEFT child of the band that already
 * carries `<checkout-disclosures priceOnly>` — the § 312j Abs. 2 BGB
 * price-incl.-VAT statement, `align="right"`, present at every basket where
 * money is owed. Being right-aligned, the statement's entire left half is
 * empty, and that empty half is where this lives: the band exists already, the
 * statement stays flush right, and nothing above or below it moves. A code
 * with its own row would spend a band of the page's highest-stakes strip, plus
 * the two gaps around it, on the minority of buyers who hold one.
 *
 * `variant="plain"` drops the button's own height floor and horizontal
 * padding, so the trigger sits flush at its neighbour's own baseline instead
 * of inside a padded control box — its `body/md` label shares the
 * statement's own 14px/20px (font/line) rung, the same eye level. Full
 * on-surface tone, not the dimmed `onVariant` a text button defaults to:
 * the statement beside it is deliberately never dimmed (§ 312j
 * "hervorgehobener Weise"), and a grey trigger next to a dark statement
 * read as two different eye levels even at matched size.
 *
 * Zero properties — it resolves its own subject out of code.state.ts, so it
 * mounts identically wherever it is placed. Low emphasis, no label, no box, no
 * glyph: findable by the buyer holding a code, near-invisible to the one who
 * is not looking for it.
 *
 * Applied adds nothing either. The discount rides the order's own lines, so
 * the § 312j statement re-sums and states the new total; the evidence that a
 * code moved it is this same run of text, on the same band, now reading the
 * buyer's own code in the accent colour instead of the invitation. A struck
 * gross beside the statutory statement was the shape considered and rejected
 * twice over: it widens the band, and § 312j asks for the total price "in
 * hervorgehobener Weise" — two figures there leave the buyer to work out which
 * one is the Gesamtpreis. The before-price belongs in the overlay's own
 * confirmation.
 *
 * Everything that genuinely changes size — the field, the spinner, the refusal
 * and its guidance — happens in voucher-code-entry.view.ts, above the page
 * rather than inside it.
 */
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { show } from "@mhmo91/schmancy/overlay";
import { html, type TemplateResult } from "lit";
import { ifDefined } from "lit/directives/if-defined.js";
import { takeUntil } from "rxjs";
import { LocalizeMixin } from "../../i18n/localize.ts";
import { voucherEntry } from "./code.state.ts";
import "./voucher-code-entry.view.ts";

@schmancyElement("voucher-code")
export class VoucherCode extends LocalizeMixin(SchmancyElement) {
	override render(): TemplateResult {
		const entry = voucherEntry.value;
		const applied = entry.phase === "applied";

		return html`
			<schmancy-button
				variant="plain"
				size="xxs"
				justify="start"
				color=${applied ? "primary" : "neutral"}
				aria-haspopup="dialog"
				aria-label=${ifDefined(
					applied
						? this.t("storefront.voucher.triggerAppliedAriaLabel", {
								code: entry.code ?? "",
							})
						: undefined,
				)}
				@click=${() =>
					// The factory form, not the eager one: the panel re-renders off
					// the same cell this run reads, so the answer lands in an overlay
					// that is already open rather than in one frozen at the press.
					show(
						() => html`
							<voucher-code-entry
								@show-tickets=${() =>
									this.dispatchEvent(
										new CustomEvent("show-tickets", {
											bubbles: true,
											composed: true,
										}),
									)}
							></voucher-code-entry>
						`,
					)
						.pipe(takeUntil(this.disconnecting))
						.subscribe()}
			>
				<schmancy-typography
					type="body"
					token="md"
					class=${this.classMap({
						"text-primary-default": applied,
						"text-surface-on": !applied,
					})}
				>
					${applied ? (entry.code ?? "") : this.t("storefront.voucher.row")}
				</schmancy-typography>
			</schmancy-button>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"voucher-code": VoucherCode;
	}
}
